import { useEffect, useMemo, useState, useRef } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PageHeader, useTitle } from "@/components/PageHeader";
import { Search, Shield, Swords, Skull, Users, Gavel, Scroll, HeartPulse, Ban, MessageSquare, MapPin, Store, Handshake, Fingerprint, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useSiteConfig } from "@/context/SiteConfig";
import { RULES, IMAGES } from "@/data/content";
import { parseBlocks, Bullets } from "@/components/RichText";

const shortLabel = (s) => s.replace(/^ch\.\s*\d+\s*[-–]\s*/i, "");
const cleanTitle = (s) => s.replace(/^(ch\.?\s*\d+|section\s+[ivx]+)\s*[-–]\s*/i, "").trim();

// Fixed sidebar category order (matches owner's rulebook — General + Sections I–V).
const RULE_CATEGORIES = ["General Rules", "Section I \u2014 Roleplay Rules", "Section II \u2014 Combat & Death", "Section III \u2014 Crime & Heists", "Section IV \u2014 Organizations", "Section V \u2014 Properties", "Other - Misc."];

// Fallback classifier used only when a rule group has no explicit `category` set in Admin.
const catFor = (name) => {
  const c = (name || "").toLowerCase();
  if (/gang|native|tribe|organization|posse|family/.test(c)) return "Organizations";
  if (/business|propert|housing|hideout|camp|store owner/.test(c)) return "Properties";
  if (/crime|larceny|heist|hogtie|stick[- ]?up|criminal|robb|the code|mugg|bounty|larcen/.test(c)) return "Crime & Heists";
  if (/value of life|self defense|vigilant|new life|nlr|violence|back-down|rdm|vdm|headpop|combat|death|fear of life/.test(c)) return "Combat & Death";
  if (/fail rp|metagam|powergam|exploit|mindshar|torture|erotic|erp|\bscene\b|roleplay/.test(c)) return "Roleplay Rules";
  if (/welcome|disclaimer|community|guideline|defined term|discord|gameplay|three strike|strike system|definitely not|recording|voice|general/.test(c)) return "General Rules";
  return "Other - Misc.";
};

// Split a category name into a short tag (Ch. 1 / Sec. II) + clean title, Penal-Code style.
const splitCat = (cat) => {
  const m = cat.match(/^\s*(ch\.?\s*\d+|section\s+[ivx]+)\s*[-–]\s*(.+)$/i);
  if (m) {
    const tag = /^ch/i.test(m[1])
      ? m[1].replace(/^ch\.?\s*/i, "Ch. ").trim()
      : "Sec. " + m[1].replace(/^section\s*/i, "").toUpperCase().trim();
    return { tag, title: m[2].trim() };
  }
  return { tag: null, title: cat.trim() };
};

// Bold the short "lead-in" phrase of a rule (e.g. "Chain Robberies: ..." or "RDM (Random Death) - ...")
const parseLead = (t) => {
  const s = (t || "").trim();
  let m = s.match(/^([A-Z][^:]{1,40}):\s*(.+)$/);
  if (m) return { lead: m[1].trim(), sep: ": ", rest: m[2].trim() };
  m = s.match(/^([A-Z][\w()/ '"-]{1,40})\s[–-]\s(.+)$/);
  if (m) return { lead: m[1].trim(), sep: " — ", rest: m[2].trim() };
  return { lead: null, sep: "", rest: s };
};

// Default warning phrases (overridden by Admin-editable site-config.warnings).
const DEFAULT_WARNINGS = [
  "strictly prohibited", "prohibited", "zero tolerance", "not allowed",
  "never allowed", "bannable", "permanent ban", "instant ban",
  "will be banned", "cop-baiting",
];

// Penalty tier tones → consistent escalating colour scale (teal → gold → ember → red).
const PENALTY_TONE = {
  info: { border: "border-teal/40", bar: "bg-teal", label: "text-teal" },
  notice: { border: "border-gold/40", bar: "bg-gold", label: "text-gold" },
  warn: { border: "border-ember/40", bar: "bg-ember", label: "text-ember" },
  danger: { border: "border-destructive/50", bar: "bg-destructive", label: "text-destructive" },
  severe: { border: "border-destructive", bar: "bg-destructive", label: "text-destructive", fill: "bg-destructive/10" },
};

const iconFor = (cat) => {
  const c = cat.toLowerCase();
  if (/rdm|vdm|violence|back-down|weapon|combat|engagement/.test(c)) return Swords;
  if (/death|new life|permadeath|murder|casualt|headpop/.test(c)) return Skull;
  if (/crime|robb|larceny|bank|fort|hostage|stick|hogtie|hood|mugg/.test(c)) return Gavel;
  if (/gang|native|tribe/.test(c)) return Users;
  if (/business|owner/.test(c)) return Store;
  if (/discord|gameplay|misc|voice/.test(c)) return MessageSquare;
  if (/metagam|powergam|exploit|mindshar/.test(c)) return Fingerprint;
  if (/value of life|self defense|vigilant|fear/.test(c)) return HeartPulse;
  if (/not allowed|definitely not|perma|strike|punish/.test(c)) return Ban;
  if (/scene|criminal rights|defined|term/.test(c)) return Scroll;
  if (/welcome|disclaimer|community|guideline/.test(c)) return Shield;
  if (/torture|erotic|erp/.test(c)) return HeartPulse;
  if (/map|county/.test(c)) return MapPin;
  return Handshake;
};

// ---- Article-mode blocks (General Rules render as written-out sections) --------
const TermsBlock = ({ items }) => {
  const intro = [], terms = [];
  items.forEach((it) => {
    const i = it.indexOf(":");
    if (i > 0 && i <= 40) terms.push({ term: it.slice(0, i).trim(), def: it.slice(i + 1).trim() });
    else intro.push(it);
  });
  return (
    <>
      {intro.map((p, k) => <p key={k} className="font-body text-[15px] leading-relaxed text-[#c8c0b4] mb-4">{p}</p>)}
      <dl className="grid gap-px bg-[#2A201A] border border-[#2A201A] sm:grid-cols-[220px_1fr]">
        {terms.map((t) => (
          <div key={t.term} className="contents">
            <dt className="bg-obsidian/80 px-5 py-4 font-feature text-sm uppercase tracking-wider text-teal">{t.term}</dt>
            <dd className="bg-surface/30 px-5 py-4 font-body text-[15px] leading-relaxed text-[#c8c0b4]">{t.def}</dd>
          </div>
        ))}
      </dl>
    </>
  );
};

const NotsBlock = ({ items }) => (
  <div className="space-y-3">
    {items.map((it, k) => {
      const raw = (it || "").replace(/\*/g, "").trim();
      if (/^definitely not allowed$/i.test(raw)) return null;
      if (raw.startsWith("##")) return <p key={k} className="font-feature text-sm uppercase tracking-[0.2em] text-ember pt-3">{raw.replace(/^#+\s*/, "")}</p>;
      if (raw.startsWith(">")) return <p key={k} className="font-body text-[15px] leading-relaxed text-[#c8c0b4]">{raw.replace(/^>\s*/, "")}</p>;
      if (/^rule:/i.test(raw)) return <p key={k} className="font-feature text-sm uppercase tracking-[0.2em] text-ember pt-2">{raw.replace(/^rule:\s*/i, "")}</p>;
      if (raw.endsWith(":") && raw.length < 46) return <p key={k} className="font-feature text-sm uppercase tracking-[0.2em] text-ember pt-2">{raw.replace(/:$/, "")}</p>;
      if (raw.length > 110) return <p key={k} className="font-body text-[15px] leading-relaxed text-[#c8c0b4]">{raw}</p>;
      return (
        <div key={k} className="flex items-start gap-3 border-l-2 border-ember/50 bg-ember/5 px-4 py-3">
          <span className="mt-2 h-1.5 w-1.5 bg-ember shrink-0" />
          <span className="text-[#c8c0b4]">{raw}</span>
        </div>
      );
    })}
  </div>
);

// Unified item renderer: "## heading", ">" or plain paragraph, "N." numbered card,
// and "- " bullets (blank line = spacing). Used in both article and accordion modes.
const RuleItems = ({ items, warnMatch, keyId = "x", article = false }) => (
  <div className="space-y-3">
    {items.map((item, ii) => {
      const raw = (item || "").trim();
      if (raw.startsWith("##")) {
        const label = raw.replace(/^#+\s*/, "");
        if (article) {
          return <h4 key={ii} className="font-feature text-sm uppercase tracking-[0.2em] text-teal pt-5 first:pt-0 mb-1">{label}</h4>;
        }
        const HIcon = iconFor(label);
        return (
          <div key={ii} className="flex items-center gap-2.5 pt-5 first:pt-0 mb-1">
            <HIcon className="h-4 w-4 text-teal shrink-0" />
            <h4 className="font-display text-lg sm:text-xl font-light text-bone">{label}</h4>
          </div>
        );
      }
      const num = raw.match(/^(\d+)\.\s+([\s\S]*)$/);
      if (num) {
        const body = num[2];
        const warn = warnMatch ? warnMatch(body) : false;
        const blocks = parseBlocks(body);
        const first = blocks[0];
        const rest = blocks.slice(1);
        const lead0 = first && first.type === "p" ? parseLead(first.text) : null;
        return (
          <div key={ii} data-testid={`rule-item-${keyId}-${ii}`}
            className={`flex gap-4 border border-[#2A201A] border-l-2 bg-obsidian/40 px-5 py-4 ${warn ? "border-l-ember bg-ember/5" : "border-l-teal/50"}`}>
            <span className={`font-mono text-sm shrink-0 tabular-nums pt-0.5 ${warn ? "text-ember" : "text-teal"}`}>{num[1]}.</span>
            <div className="flex-1 space-y-3 text-[#c8c0b4] leading-relaxed">
              {first && (lead0
                ? <p>{lead0.lead && <span className="font-semibold text-bone">{lead0.lead}</span>}{lead0.sep}{lead0.rest}</p>
                : <Bullets items={first.items} />)}
              {rest.map((b, bi) => (b.type === "list" ? <Bullets key={bi} items={b.items} /> : <p key={bi}>{b.text}</p>))}
            </div>
          </div>
        );
      }
      const body = raw.replace(/^>\s*/, "");
      const blocks = parseBlocks(body);
      return (
        <div key={ii} className="space-y-3">
          {blocks.map((b, bi) => (b.type === "list"
            ? <div key={bi} className="text-[#c8c0b4]"><Bullets items={b.items} /></div>
            : (() => { const l = parseLead(b.text); return <p key={bi} className="font-body text-[15px] leading-relaxed text-[#c8c0b4]">{l.lead && <span className="font-semibold text-bone">{l.lead}</span>}{l.sep}{l.rest}</p>; })()))}
        </div>
      );
    })}
  </div>
);

const RuleArticle = ({ g, keyId, setRef, warnMatch }) => {
  const isNots = /definitely not|prohibited & restricted/i.test(g.cat);
  const Icon = isNots ? Ban : iconFor(g.cat);
  const title = cleanTitle(g.cat);
  return (
    <div ref={setRef} data-testid={`rule-group-${keyId}`} className="scroll-mt-24">
      <div className={`flex items-center gap-3 border-b pb-3 mb-5 ${isNots ? "border-ember/40" : "border-teal/30"}`}>
        <span className={`flex h-9 w-9 items-center justify-center border bg-obsidian shrink-0 ${isNots ? "border-ember/50 text-ember" : "border-[#2A201A] text-teal"}`}><Icon className="h-4 w-4" /></span>
        <h3 className={`font-display text-2xl sm:text-3xl font-light ${isNots ? "text-ember" : "text-bone"}`}>{title}</h3>
      </div>
      {/defined terms/i.test(g.cat) ? <TermsBlock items={g.items} />
        : isNots ? <NotsBlock items={g.items} />
        : <RuleItems items={g.items} warnMatch={warnMatch} keyId={keyId} article />}
    </div>
  );
};

export default function Rules() {
  useTitle("Server Rules | Aurora Springs RP | RedM Roleplay", "Read the community and roleplay rules for Aurora Springs RP, an 18+ whitelisted RedM server — roleplay standards, combat, crime, organizations and property rules.");
  const { img, warnings, penal_text } = useSiteConfig();
  const [groups, setGroups] = useState(RULES);
  const [penalties, setPenalties] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState("");
  const refs = useRef({});
  const penaltyNote = penal_text?.penalty_note;

  useEffect(() => {
    api.get("/rules").then(({ data }) => { if (data?.length) setGroups(data); }).catch(() => {});
    api.get("/penalties").then(({ data }) => { if (data?.length) setPenalties(data); }).catch(() => {});
  }, []);

  const warnMatch = useMemo(() => {
    const ps = (warnings?.length ? warnings : DEFAULT_WARNINGS).map((p) => p.toLowerCase()).filter(Boolean);
    return (t) => { const s = (t || "").toLowerCase(); return ps.some((p) => s.includes(p)); };
  }, [warnings]);

  // Group rules by their category (explicit in Admin, else auto-classified), in the fixed order.
  const categories = useMemo(() => {
    const byCat = {};
    for (const g of groups) {
      const cat = (g.category && g.category.trim()) || catFor(g.cat);
      (byCat[cat] ||= []).push(g);
    }
    const ordered = RULE_CATEGORIES.filter((c) => byCat[c]).map((c) => ({ label: c, groups: byCat[c] }));
    Object.keys(byCat).filter((c) => !RULE_CATEGORIES.includes(c)).forEach((c) => ordered.push({ label: c, groups: byCat[c] }));
    return ordered;
  }, [groups]);

  const s = q.trim().toLowerCase();
  const matches = (g) => !s || g.cat.toLowerCase().includes(s) || g.items.some((i) => i.toLowerCase().includes(s));
  const visibleCats = categories
    .map((c) => ({ ...c, groups: c.groups.filter(matches) }))
    .filter((c) => c.groups.length);

  const jump = (g) => {
    setOpen(g.id || g.cat);
    setTimeout(() => refs.current[g.id || g.cat]?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  return (
    <>
      <PageHeader overline="Code of the Frontier" title="Rules" image={img("rules", IMAGES.ridge)}
        sub="Aurora Springs runs on trust and immersion. Search the code below or browse by chapter — read carefully before you ride in." />

      <section className="mx-auto max-w-7xl px-6 sm:px-10 py-14 grid gap-10 lg:grid-cols-[260px_1fr] items-start">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 hidden lg:block max-h-[80vh] overflow-y-auto pr-2" data-testid="rules-sidebar">
          {visibleCats.map((c) => (
            <div key={c.label} className="mb-8">
              <p className="font-feature text-[10px] uppercase tracking-[0.25em] text-[#8F877C] mb-3">{c.label}</p>
              <ul className="space-y-1">
                {c.groups.map((g) => (
                  <li key={g.id || g.cat}>
                    <button onClick={() => jump(g)} data-testid={`rules-nav-${g.id || g.cat}`}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${open === (g.id || g.cat) ? "bg-teal text-obsidian font-medium" : "text-[#b8b0a4] hover:text-bone hover:bg-surface/60"}`}>
                      {cleanTitle(g.cat)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </aside>

        {/* Main */}
        <div>
          <div className="relative mb-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8F877C]" />
            <input data-testid="rules-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rules..."
              className="w-full bg-surface/60 border border-[#2A201A] pl-11 pr-4 py-3.5 text-sm text-bone placeholder:text-[#5c554c] focus:outline-none focus:border-teal transition-colors" />
          </div>

          {visibleCats.length === 0 && <div className="py-16 text-center font-mono text-sm text-[#8F877C]">No rules match "{q}".</div>}

          {/* General Rules — written-out article sections */}
          {visibleCats.filter((c) => c.label === "General Rules").map((c) => (
            <div key={c.label} className="mb-14">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal mb-6">{c.label}</p>
              <div className="space-y-12">
                {c.groups.map((g) => {
                  const key = g.id || g.cat;
                  return <RuleArticle key={key} g={g} keyId={key} setRef={(el) => (refs.current[key] = el)} warnMatch={warnMatch} />;
                })}
              </div>
            </div>
          ))}

          <Accordion type="single" collapsible value={open} onValueChange={setOpen} className="space-y-8">
            {visibleCats.filter((c) => c.label !== "General Rules").map((c) => (
              <div key={c.label}>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal mb-4">{c.label}</p>
                <div className="space-y-3">
                  {c.groups.map((g) => {
                    const Icon = iconFor(g.cat);
                    const key = g.id || g.cat;
                    const { tag, title } = splitCat(g.cat);
                    return (
                      <div key={key} ref={(el) => (refs.current[key] = el)} className="scroll-mt-24">
                        <AccordionItem value={key} data-testid={`rule-group-${key}`}
                          className="border border-[#2A201A] bg-surface/40 px-5 data-[state=open]:border-teal/40 transition-colors">
                          <AccordionTrigger className="hover:no-underline py-5">
                            <div className="flex items-center gap-4 text-left">
                              <span className="flex h-9 w-9 items-center justify-center border border-[#2A201A] bg-obsidian text-teal shrink-0"><Icon className="h-4 w-4" /></span>
                              <span className="flex flex-col gap-0.5">
                                {tag && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal">{tag}</span>}
                                <span className="font-display text-xl sm:text-2xl font-light leading-tight">{title}</span>
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="pb-2">
                              <RuleItems items={g.items} warnMatch={warnMatch} keyId={key} />
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </Accordion>
        </div>
      </section>

      {penalties.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 sm:px-10 pb-20" data-testid="penalty-system">
          <div className="flex items-center gap-3 border-b border-[#2A201A] pb-4 mb-8">
            <span className="flex h-9 w-9 items-center justify-center border border-[#2A201A] bg-obsidian text-gold shrink-0"><Ban className="h-4 w-4" /></span>
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Enforcement</p>
              <h2 className="font-display text-3xl sm:text-4xl font-light">Penalty System</h2>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {penalties.map((p) => {
              const t = PENALTY_TONE[p.tone] || PENALTY_TONE.info;
              return (
                <div key={p.id} data-testid={`penalty-${p.order}`}
                  className={`relative border ${t.border} ${t.fill || "bg-surface/40"} p-5 overflow-hidden`}>
                  <span className={`absolute left-0 top-0 h-full w-1 ${t.bar}`} />
                  <p className={`font-feature text-xs uppercase tracking-[0.2em] ${t.label} mb-2`}>{p.label}</p>
                  <p className="font-display text-lg font-light text-bone leading-snug">{p.detail}</p>
                </div>
              );
            })}
          </div>
          {penaltyNote && (
            <p className="mt-6 flex items-start gap-3 border-l-2 border-gold/50 bg-gold/5 px-4 py-3 text-sm text-[#c9c0b0]">
              <AlertTriangle className="h-4 w-4 text-gold shrink-0 mt-0.5" />{penaltyNote}
            </p>
          )}
        </section>
      )}
    </>
  );
}
